import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { WidgetCard } from "@/components/Widgets/WidgetCard";
import { FilterBar } from "@/components/Widgets/FilterBar";
import type { Widget } from "@/components/Widgets/kinds/types";
import { useNotifications } from "@/notifications/NotificationsProvider";
import { apiFetch } from "@/auth/token";
import { friendlyError } from "@/api/friendlyError";

export function Dashboard() {
  // WorkspaceLayout owns NotificationsProvider; the hooks below run inside it.
  return <DashboardBody />;
}

function DashboardBody() {
  const apiUrl = import.meta.env.VITE_API_URL ?? "";
  const { subscribe } = useNotifications();
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterValues, setFilterValues] = useState<Record<string, unknown>>({});
  const [refreshing, setRefreshing] = useState(false);

  const fetchOne = useCallback(
    async (id: string): Promise<Widget | null> => {
      try {
        const r = await apiFetch(`${apiUrl}/v1/widgets`);
        if (!r.ok) return null;
        const all = (await r.json()) as Widget[];
        return all.find((w) => w.id === id) ?? null;
      } catch {
        return null;
      }
    },
    [apiUrl],
  );

  // Initial load.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch(`${apiUrl}/v1/widgets`);
        if (!r.ok) throw new Error(friendlyError(r.status));
        const rows = (await r.json()) as Widget[];
        if (!cancelled) {
          setWidgets(rows);
          // Seed filter values from union of param defaults.
          const seed: Record<string, unknown> = {};
          for (const w of rows) {
            for (const p of w.parameters ?? []) {
              if (!(p.name in seed) && p.default !== undefined) {
                seed[p.name] = p.default;
              }
            }
          }
          setFilterValues(seed);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl]);

  // Live appends/updates/deletes via /v1/events.
  useEffect(() => {
    return subscribe(async (ev) => {
      if (ev.type !== "widget_event") return;
      const p = ev.payload as { action?: string; widget_id?: string };
      if (!p.widget_id) return;
      if (p.action === "deleted") {
        setWidgets((cur) => cur.filter((w) => w.id !== p.widget_id));
        return;
      }
      // created or updated: fetch fresh row
      const w = await fetchOne(p.widget_id);
      if (!w) return;
      setWidgets((cur) => {
        const i = cur.findIndex((x) => x.id === w.id);
        if (i === -1) return [...cur, w];
        const copy = [...cur];
        copy[i] = w;
        return copy;
      });
    });
  }, [subscribe, fetchOne]);

  // Debounced filter refresh.
  const debounceRef = useRef<number | null>(null);
  useEffect(() => {
    if (loading) return;
    if (widgets.length === 0) return;
    if (debounceRef.current !== null) {
      window.clearTimeout(debounceRef.current);
    }
    debounceRef.current = window.setTimeout(async () => {
      setRefreshing(true);
      try {
        const r = await apiFetch(`${apiUrl}/v1/widgets/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filter_values: filterValues }),
        });
        if (r.ok) {
          const rows = (await r.json()) as Widget[];
          setWidgets(rows);
        }
      } finally {
        setRefreshing(false);
      }
    }, 350);
    return () => {
      if (debounceRef.current !== null) {
        window.clearTimeout(debounceRef.current);
      }
    };
  // intentionally watching filterValues only — initial load handles first paint
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, apiUrl]);

  const headerSubtitle = useMemo(() => {
    if (refreshing) return "Refreshing widgets…";
    if (widgets.length === 0)
      return "Live widgets from Artie. Ask in chat to build one.";
    return `${widgets.length} widget${widgets.length === 1 ? "" : "s"} · live from Toast`;
  }, [refreshing, widgets.length]);

  return (
    <>
      <Box
        data-ph-scope="header"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
          pb: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.125rem" },
              background:
                "linear-gradient(135deg, #0f1110 0%, #1f7a66 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Dashboard
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, fontWeight: 500 }}
          >
            {headerSubtitle}
          </Typography>
        </Box>
        <Button
          size="medium"
          variant="contained"
          disableElevation
          data-ph="create_defaults"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 2.5,
            width: { xs: "100%", sm: "auto" },
            background: "linear-gradient(135deg, #1f7a66 0%, #10b981 100%)",
            boxShadow: "0 4px 14px rgba(31, 122, 102, 0.25)",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(31, 122, 102, 0.35)",
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease",
          }}
          onClick={async () => {
            const r = await apiFetch(`${apiUrl}/v1/widgets/seed-defaults`, {
              method: "POST",
            });
            if (r.ok) {
              const rows = (await r.json()) as Widget[];
              setWidgets(rows);
              // Re-seed filter values from new param defaults (only when
              // current filterValues are empty — don't clobber active picks).
              const hasValues = Object.values(filterValues).some(
                (v) => v !== undefined && v !== null && v !== "",
              );
              if (!hasValues) {
                const seed: Record<string, unknown> = {};
                for (const w of rows) {
                  for (const p of w.parameters ?? []) {
                    if (!(p.name in seed) && p.default !== undefined) {
                      seed[p.name] = p.default;
                    }
                  }
                }
                setFilterValues(seed);
              }
            }
          }}
        >
          Populate with defaults
        </Button>
      </Box>

      <FilterBar
        widgets={widgets}
        values={filterValues}
        onChange={setFilterValues}
      />

      {loading && (
        <Stack alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {!loading && error && (
        <Typography color="error" variant="body2">
          Failed to load widgets: {error}
        </Typography>
      )}

      {!loading && !error && widgets.length === 0 && (
        <Stack alignItems="center" spacing={2} sx={{ py: 6, textAlign: "center" }} data-ph-scope="empty">
          <Typography variant="body1" color="text.secondary">No widgets yet.</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 480 }}>
            Open the chat and ask Artie to “build a sales-by-daypart chart” or
            “show me revenue by location”. The widget will appear here.
          </Typography>
          <Button component="a" href="/workspace" variant="contained" size="small" data-ph="chat">
            Open chat
          </Button>
        </Stack>
      )}

      {!loading && !error && widgets.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gridAutoRows: "min-content",
            alignItems: "stretch",
            gap: 2.5,
            mt: 1,
          }}
        >
          {[...widgets].sort((a, b) => {
            const rank = (k: string) => (k === "kpi" ? 0 : 1);
            return rank(a.kind) - rank(b.kind);
          }).map((w) => (
            <Box
              key={w.id}
              sx={{
                minHeight: 0,
                // KPIs compact (2 cols), tables 2 cols, every chart full row (4 cols)
                // so axes + legends breathe.
                gridColumn: {
                  md:
                    w.kind === "kpi"
                      ? "span 1"
                      : w.kind === "bars" || w.kind === "hbars"
                        ? "span 4"
                        : "span 2",
                },
              }}
            >
              <WidgetCard
                widget={w}
                onDelete={async (id) => {
                  const prev = widgets;
                  setWidgets((cur) => cur.filter((x) => x.id !== id));
                  try {
                    const r = await apiFetch(`${apiUrl}/v1/widgets/${id}`, {
                      method: "DELETE",
                    });
                    if (!r.ok && r.status !== 204) throw new Error(friendlyError(r.status));
                  } catch {
                    setWidgets(prev);
                  }
                }}
              />
            </Box>
          ))}
        </Box>
      )}
    </>
  );
}
